import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const ContactForm = () => {
  const { lang, t } = useLanguage();
  
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    emailAddress: '',
    moveInDate: '',
    occupants: '',
    tourDate: '',
    message: '',
    consent: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const formatToE164 = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null;
  };

  const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (submitError) setSubmitError(null);

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = t.contact.errName;
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t.contact.errPhone;
    } else {
      const formatted = formatToE164(formData.phoneNumber);
      if (!formatted) {
        newErrors.phoneNumber = t.contact.errPhoneInvalid;
      }
    }
    
    // Email is optional now, but validate format if entered
    if (formData.emailAddress.trim() && !/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = t.contact.errEmail2;
    }
    
    if (!formData.consent) newErrors.consent = t.contact.errConsent;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getUrlParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    let hashParams = new URLSearchParams('');
    if (window.location.hash.includes('?')) {
      hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
    }
    const getParam = (key) => searchParams.get(key) || hashParams.get(key) || '';
    
    return {
      utm_source: getParam('utm_source'),
      utm_medium: getParam('utm_medium'),
      utm_campaign: getParam('utm_campaign'),
      utm_content: getParam('utm_content'),
      utm_term: getParam('utm_term'),
      fbclid: getParam('fbclid')
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      setSubmitError(null);

      const formattedPhone = formatToE164(formData.phoneNumber);
      const firstName = formData.fullName.trim().split(' ')[0] || 'there';
      const submittedAt = new Date().toISOString();
      const uuid = generateUUID();
      const trackingParams = getUrlParams();
      
      const renterSmsEn = `Hi ${firstName}, thank you for your interest in the townhouse available for $2,800 per month. We received your inquiry and will contact you soon to discuss the property and schedule a tour. You may call or text 786-529-1915. Reply STOP to opt out.`;
      const renterSmsEs = `Hola ${firstName}, gracias por su interés en el townhouse disponible por $2,800 mensuales. Recibimos su solicitud y nos comunicaremos pronto para conversar sobre la propiedad y coordinar una visita. Puede llamar o enviar un mensaje al 786-529-1915. Responda STOP para dejar de recibir mensajes.`;
      
      const ownerSms = `New townhouse rental inquiry\n\nName: ${formData.fullName}\nPhone: ${formattedPhone}\nEmail: ${formData.emailAddress}\nLanguage: ${lang === 'en' ? 'English' : 'Spanish'}\nDesired move-in: ${formData.moveInDate || 'Not provided'}\nOccupants: ${formData.occupants || 'Not provided'}\nPreferred tour: ${formData.tourDate || 'Not provided'}\nMessage: ${formData.message || 'Not provided'}\nSubmitted: ${submittedAt}`;

      const payload = {
        submission_id: uuid,
        submitted_at: submittedAt,
        source: "townhouse_rental_landing_page",
        language: lang,
        language_label: lang === 'en' ? 'English' : 'Spanish',
        full_name: formData.fullName,
        phone: formattedPhone,
        email: formData.emailAddress,
        desired_move_in_date: formData.moveInDate || '',
        number_of_occupants: formData.occupants || '',
        preferred_tour_datetime: formData.tourDate || '',
        message: formData.message || '',
        contact_consent: formData.consent,
        page_url: window.location.href,
        advertised_monthly_rent: 2800,
        owner_phone: "+17865291915",
        renter_confirmation_sms: lang === 'en' ? renterSmsEn : renterSmsEs,
        owner_notification_sms: ownerSms,
        ...trackingParams
      };

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 seconds timeout

        const response = await fetch('https://cloud.activepieces.com/api/v1/webhooks/4p0N6IySQEgmJ2P5lrYlv', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (response.ok) {
          if (window.fbq) {
            window.fbq('track', 'Lead', {
              content_name: 'Townhouse Rental Inquiry',
              value: 2800,
              currency: 'USD',
              language: lang
            });
          }
          setIsSubmitted(true);
          setFormData({
            fullName: '',
            phoneNumber: '',
            emailAddress: '',
            moveInDate: '',
            occupants: '',
            tourDate: '',
            message: '',
            consent: false
          });
          setShowMoreDetails(false);
        } else {
          console.error("Webhook returned non-OK status:", response.status);
          setSubmitError(t.contact.submitError);
        }
      } catch (err) {
        console.error("Webhook submission error:", err);
        setSubmitError(t.contact.submitError);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section id="contact" className="section-padding bg-slate-50 mt-8 mb-12">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-6 md:p-10 relative z-20 -mt-16 md:-mt-24 lg:-mt-32">
            {isSubmitted ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                  <SafeIcon name="Check" className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{t.contact.successTitleUpdate || t.contact.successTitle}</h3>
                <button 
                  onClick={() => setIsSubmitted(false)}
                  className="btn-secondary"
                >
                  {t.contact.successBtn}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-1">
                      {t.contact.name}
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl border ${errors.fullName ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-brand'} focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`}
                      placeholder={t.contact.phName}
                    />
                    {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-700 mb-1">
                      {t.contact.phone}
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl border ${errors.phoneNumber ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-brand'} focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`}
                      placeholder="(555) 123-4567"
                    />
                    {errors.phoneNumber && <p className="mt-1 text-sm text-red-500">{errors.phoneNumber}</p>}
                  </div>

                  {/* Move-in Date */}
                  <div>
                    <label htmlFor="moveInDate" className="block text-sm font-medium text-slate-700 mb-1">
                      {t.contact.date}
                    </label>
                    <input
                      type="date"
                      id="moveInDate"
                      name="moveInDate"
                      value={formData.moveInDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-brand focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                    />
                  </div>

                  {/* Occupants */}
                  <div>
                    <label htmlFor="occupants" className="block text-sm font-medium text-slate-700 mb-1">
                      {t.contact.occ}
                    </label>
                    <select
                      id="occupants"
                      name="occupants"
                      value={formData.occupants}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-brand focus:outline-none focus:ring-2 focus:border-transparent transition-shadow bg-white"
                    >
                      <option value="">{t.contact.select}</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>
                </div>

                {/* Add More Details Toggle */}
                <button
                  type="button"
                  onClick={() => setShowMoreDetails(!showMoreDetails)}
                  className="flex items-center text-brand font-semibold text-sm hover:text-brand-dark transition-colors"
                >
                  {t.contact.addMoreDetails}
                  <SafeIcon name={showMoreDetails ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 ml-1" />
                </button>

                <AnimatePresence>
                  {showMoreDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 pb-2">
                        {/* Email */}
                        <div className="md:col-span-2">
                          <label htmlFor="emailAddress" className="block text-sm font-medium text-slate-700 mb-1">
                            {t.contact.email}
                          </label>
                          <input
                            type="email"
                            id="emailAddress"
                            name="emailAddress"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-xl border ${errors.emailAddress ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-brand'} focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`}
                            placeholder="john@example.com"
                          />
                          {errors.emailAddress && <p className="mt-1 text-sm text-red-500">{errors.emailAddress}</p>}
                        </div>

                        {/* Tour Date */}
                        <div className="md:col-span-2">
                          <label htmlFor="tourDate" className="block text-sm font-medium text-slate-700 mb-1">
                            {t.contact.tour}
                          </label>
                          <input
                            type="text"
                            id="tourDate"
                            name="tourDate"
                            value={formData.tourDate}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-brand focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                            placeholder={t.contact.phDate}
                          />
                        </div>

                        {/* Message */}
                        <div className="md:col-span-2">
                          <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                            {t.contact.msg}
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows="4"
                            value={formData.message}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-brand focus:outline-none focus:ring-2 focus:border-transparent transition-shadow resize-none"
                            placeholder={t.contact.phMsg}
                          ></textarea>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Consent */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="consent"
                      name="consent"
                      type="checkbox"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                      className="w-5 h-5 text-brand bg-slate-100 border-slate-300 rounded focus:ring-brand focus:ring-2"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="consent" className="font-medium text-slate-700">
                      {t.contact.consent}
                    </label>
                    {errors.consent && <p className="mt-1 text-red-500">{errors.consent}</p>}
                  </div>
                </div>

                {/* Submit Error */}
                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-600 text-sm font-medium">{submitError}</p>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-8 py-4 text-lg font-bold rounded-xl text-white bg-brand hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-all shadow-md ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? t.contact.btnSending : t.contact.btnCheck}
                  </button>
                  <p className="text-xs text-slate-500 mt-4 text-center">
                    {t.contact.note}
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;